import React, { useEffect, useState } from "react";
import Offcanvas from "olive-react-myoneui/lib/Offcanvas";
import Button from "olive-react-myoneui/lib/Button";
import { useTranslation } from "react-i18next";
import ProgressBar from "olive-react-myoneui/lib/ProgressBar";
import Form from "olive-react-myoneui/lib/Form";
import FloatingLabel from "olive-react-myoneui/lib/FloatingLabel";
import Card from "olive-react-myoneui/lib/Card";
import Row from "olive-react-myoneui/lib/Row";
import Col from "olive-react-myoneui/lib/Col";
import { useDispatch } from "react-redux";
import { setConfigData } from "../../../../reducers/configSlice";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { externalDocsMutation, getSingleExternalDocs } from "../../../../api/afterLogin/externalDocs";
import { tagCloseIcon } from "../../../constants/icons";

const OffcanvasDocUploads = ({ show, setShow, triggerText, accessToken, id }) => {
  const dispatch = useDispatch();
  const [blocks, setBlocks] = useState([{ id: 1 }]);
  const [blockFiles, setBlockFiles] = useState("");
  const [title, setTitle] = useState("");
  const [issuingAuthority, setIssuingAuthority] = useState("");
  const [expiryDate, setExpiryDate] = useState(null);
  const [issueDate, setIssueDate] = useState(null);
  const [expiryDateValid, setExpiryDateValid] = useState(true);
  const [issueDateValid, setIssueDateValid] = useState(true);
  const [validated, setValidated] = useState(false);
  const [isPrevImg, setIsPrevImg] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [fileTypeError, setFileTypeError] = useState(false);
  const { t } = useTranslation();

  const handleClose = () => {
    setShow(false);
  };

  const handleSingleGet = async (externalDocId) => {
    if (externalDocId != 0) {
      let data = JSON.stringify({
        id: externalDocId,
      });

      const singleExternalDocData = await getSingleExternalDocs(data, accessToken);
      const issuedDate =
        singleExternalDocData.ed_issued_date != "0000-00-00"
          ? singleExternalDocData.ed_issued_date
          : null;
      const expiryDate =
        singleExternalDocData.ed_expiry_date != "0000-00-00"
          ? singleExternalDocData.ed_expiry_date
          : null;
      setBlockFiles(singleExternalDocData.ed_filename);
      setTitle(singleExternalDocData.ed_name);
      setIssueDate(issuedDate != null ? new Date(singleExternalDocData.ed_issued_date) : null);
      setIssuingAuthority(singleExternalDocData.ed_issuing_authority);
      setExpiryDate(expiryDate != null ? new Date(expiryDate) : null);
    }
  };

  const offCanvasKeyDown = (e) => e.stopPropagation();

  useEffect(() => {
    if (triggerText === "Edit") {
      handleSingleGet(id);
      setIsPrevImg(true);
    } else {
      setBlockFiles("");
      setTitle("");
      setIssueDate(null);
      setIssuingAuthority("");
      setExpiryDate(null);
      setIsPrevImg(false);
    }
  }, [show]);

  useEffect(() => {
    if (triggerText === "Edit") {
      setIsPrevImg(true);
    } else {
      setIsPrevImg(false);
    }
  }, []);

  const formattedDate = (date) => {
    const formatDate = new Date(date);
    const year = formatDate.getFullYear();
    const month = String(formatDate.getMonth() + 1).padStart(2, "0");
    const day = String(formatDate.getDate()).padStart(2, "0");
    const hours = String(formatDate.getHours()).padStart(2, "0");
    const minutes = String(formatDate.getMinutes()).padStart(2, "0");
    const seconds = String(formatDate.getSeconds()).padStart(2, "0");

    if (year == "1970") {
      return `0000-00-00 00:00:00`;
    } else {
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.currentTarget;

    if (issueDate == null) {
      setIssueDateValid(false);
      return false;
    } else {
      setIssueDateValid(true);
    }

    if (form.checkValidity() === false) {
      event.stopPropagation();
      setValidated(true);
      return false;
    }

    if (form.checkValidity() === true) {
      setIsLoading(true);
    }

    let formData = new FormData();
    formData.append("docs", blockFiles);
    formData.append("title", title);
    formData.append("issuing_authority", issuingAuthority);
    formData.append("issued_date", formattedDate(issueDate));
    formData.append("expiry_date", formattedDate(expiryDate));
    if (triggerText === "Edit") {
      formData.append("id", id);
    }

    const response = await externalDocsMutation(formData, accessToken, triggerText);
    if(response === true) {
      handleClose();
      setIsLoading(false);
      dispatch(setConfigData({ success_doc: true }));
      if (triggerText != "Edit") {
        setBlockFiles("");
        setTitle("");
        setIssueDate(null);
        setIssuingAuthority("");
        setExpiryDate(null);
      } else {
        setIsPrevImg(true);
      }
    }
  };

  // Files Selection
  const handleFileUpload = (event) => {
    const uploadedFile = event.target.files[0];
    if (uploadedFile) {
      const allowedTypes = [
        "image/png",
        "image/jpeg",
        "application/msword",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/pdf",
      ];

      if (!allowedTypes.includes(uploadedFile.type)) {
        event.target.value = "";
        setFileTypeError(true);
        setBlockFiles(false);
      } else {
        setFileTypeError(false);
        setBlockFiles(uploadedFile);
      }
    }
  };

  const handleTitle = (event) => {
    const text = event.target.value;
    setTitle(text);
  };

  const handleIssuingAuthority = (event) => {
    const issuingAuthority = event.target.value;
    setIssuingAuthority(issuingAuthority);
  };

  const handleIssueDate = (date) => {
    setIssueDate(date);
    if (expiryDate && date && expiryDate < date) {
      setExpiryDate(null);
      setExpiryDateValid(true);
    }
  };

  const handleExpiryDate = (date) => {
    if (date && issueDate && date < issueDate) {
      setExpiryDateValid(false);
      return;
    }
    setExpiryDate(date);
    setExpiryDateValid(true);
  };

  const handlePrevImgRemove = () => {
    setIsPrevImg(false);
    setBlockFiles("");
  };

  return (
    <>
      <Offcanvas
        show={show}
        onHide={handleClose}
        placement="end"
        className="pb-0 res-notification myk__stu-doc-offcanvas"
        onKeyDown={offCanvasKeyDown}
      >
        <Form noValidate validated={validated} onSubmit={handleSubmit}>
          <Offcanvas.Header
            closeButton
            backButton={`${t("Back")}`}
            className="pb-0"
          >
            <Offcanvas.Title>
              {triggerText === "Edit" ? "Edit" : "Upload"} Your Documents
            </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <Row className="mb-3">
              <Col lg={8}>
                <p>Upload your documents in your portfolio.</p>
              </Col>
              <Col lg={4} className="text-end"></Col>
            </Row>
            <div className="files-block-wrapper">
              {blocks.map((block) => (
                <Card
                  className="single-file-block mb-3 flex-column border-0"
                  key={block.id}
                >
                  <Card.Body className="bg-ghost-white rounded">
                    {/* Document Title */}
                    <FloatingLabel
                      controlId={`doc-name-${block.id}`}
                      label="Document Title"
                      className="mb-3"
                    >
                      <Form.Control
                        required
                        type="text"
                        placeholder="Document Title"
                        onChange={handleTitle}
                        value={title}
                        defaultValue={title}
                      />
                      <Form.Control.Feedback type="invalid">
                        Please provide document title.
                      </Form.Control.Feedback>
                    </FloatingLabel>

                    {/* Issuing Authority */}
                    <FloatingLabel
                      controlId={`issuing-authority-${block.id}`}
                      label="Issuing Authority"
                      className="mb-3"
                    >
                      <Form.Control
                        type="text"
                        placeholder="Issuing Authority"
                        onChange={handleIssuingAuthority}
                        value={issuingAuthority}
                        defaultValue={
                          triggerText === "Edit" ? issuingAuthority : ""
                        }
                      />
                      <Form.Control.Feedback type="invalid">
                        Please provide Issuing Authority.
                      </Form.Control.Feedback>
                    </FloatingLabel>

                    {/* Issuing Date */}
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DesktopDatePicker
                        label="Issuing Date"
                        className="w-100 mb-3 mk__stu-issue-date"
                        value={issueDate || null}
                        onChange={handleIssueDate}
                        defaultValue={triggerText === "Edit" ? issueDate : null}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: 48,
                            borderRadius: 2,
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor:
                                "rgba(var(--bs-primary-color), 0.75)",
                            },
                            "&.Mui-focused .MuiInputLabel-root": {
                              color: "rgba(var(--bs-primary-color), 0.75)",
                            },
                          },
                          "& .MuiInputBase-input": {
                            fontSize: "0.75rem",
                            fontFamily: "Mulish",
                            fontWeight: 600,
                            color: "var(--bs-dark-text-color)",
                            "& :placeholder": {
                              color: "var(--bs-dark-text-color)"
                            }
                          },
                          "& .MuiInputLabel-root": {
                            fontSize: "0.75rem",
                            fontFamily: "Mulish",
                            fontWeight: 600,
                            color: "var(--bs-dark-text-color)",
                            "&.Mui-focused": {
                              color: "rgba(var(--bs-primary-color), 0.75)", // Change label color on focus
                              transform: "translate(17px, -7px) scale(0.75)",
                            },
                          },
                          "& .MuiButtonBase-root": {
                            color: "var(--bs-dark-text-color)"
                          }
                        }}
                      />
                    </LocalizationProvider>
                    <div
                      className={`invalid-feedback ${
                        issueDateValid ? "d-none" : "d-block mb-3"
                      }`}
                    >
                      Please provide document issuing date.
                    </div>

                    {/* Expiry Date */}
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DesktopDatePicker
                        label="Expiry Date"
                        className="w-100 mk__stu-issue-date"
                        value={expiryDate}
                        onChange={handleExpiryDate}
                        minDate={issueDate}
                        defaultValue={
                          triggerText === "Edit" ? expiryDate : null
                        }
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: 48,
                            borderRadius: 2,
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                              borderColor:
                                "rgba(var(--bs-primary-color), 0.75)",
                            },
                            "&.Mui-focused .MuiInputLabel-root": {
                              color: "rgba(var(--bs-primary-color), 0.75)",
                            },
                          },
                          "& .MuiInputBase-input": {
                            fontSize: "0.75rem",
                            fontFamily: "Mulish",
                            fontWeight: 600,
                            color: "var(--bs-dark-text-color)",
                            "& :placeholder": {
                              color: "var(--bs-dark-text-color)"
                            }
                          },
                          "& .MuiInputLabel-root": {
                            fontSize: "0.75rem",
                            fontFamily: "Mulish",
                            fontWeight: 600,
                            color: "var(--bs-dark-text-color)",
                            "&.Mui-focused": {
                              color: "rgba(var(--bs-primary-color), 0.75)", // Change label color on focus
                              transform: "translate(17px, -7px) scale(0.75)",
                            },
                          },
                          "& .MuiButtonBase-root": {
                            color: "var(--bs-dark-text-color)"
                          }
                        }}
                      />
                    </LocalizationProvider>
                    <div
                      className={`invalid-feedback  ${
                        expiryDateValid ? "d-none" : "d-flex mb-3"
                      }`}
                    >
                      Expiry date must be greater than issue date.
                    </div>

                    {/* Upload your files */}
                    {isPrevImg ? (
                      <>
                        <div className="files-upload-wrapper mt-3 mb-2">
                          <div className="information-block information-block-sm-pd bg-ghost-white mb-3 p-0">
                            <div className="information-wrap">
                              <div className="information-holder">
                                <div className="information-detail">
                                  <h4>{title} &nbsp;|&nbsp; {`(${blockFiles})`}</h4>
                                </div>
                              </div>
                              <Button
                                variant="orange"
                                className="btn-round d-inline-flex align-items-center gap-1 text-white"
                                size="sm"
                                onClick={handlePrevImgRemove}
                              >
                              {tagCloseIcon} Remove
                              </Button>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <Card className="border-0 p-0 mt-4 mb-1 bg-transparent">
                        <Card.Header className="p-0">
                          <strong>Upload your files</strong>
                          <Row>
                            <Col lg={7}>
                              <p className="help-text mb-1">
                                Consider contributing to your portfolio by
                                providing the necessary files for upload. This
                                makes your portfolio better.
                              </p>
                            </Col>
                            <Col lg={5} className="text-end">
                              <Form.Group controlId={`upload-file-${id}`}>
                                <Form.Control
                                  type="file"
                                  required
                                  className="d-none"
                                  placeholder="Upload Document"
                                  onChange={handleFileUpload}
                                  accept="image/png, image/jpeg, .doc,.docx,.pdf"
                                />
                                <Form.Control.Feedback
                                  className="label-file-upload"
                                  type="invalid"
                                >
                                  Please, upload your document.
                                </Form.Control.Feedback>
                                {fileTypeError && (
                                  <Form.Control.Feedback className="label-file-upload label-invalid-files-message d-block">
                                    Please upload valid files (PNG, JPEG, DOC,
                                    DOCX, or PDF).
                                  </Form.Control.Feedback>
                                )}
                                <Form.Label className="btn-getupload-file">
                                  Upload Document
                                </Form.Label>
                              </Form.Group>
                            </Col>
                          </Row>
                        </Card.Header>
                        {blockFiles && (
                          <Card.Body className="pt-3 p-0">
                            <div className="files-upload-wrapper my-0">
                              <div className="information-block information-block-sm-pd bg-ghost-white mb-1">
                                <div className="information-wrap">
                                  <div className="information-holder">
                                    <div className="information-detail">
                                      <h4>{blockFiles.name}</h4>
                                      <ProgressBar now={100} />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </Card.Body>
                        )}
                      </Card>
                    )}
                  </Card.Body>
                  <Card.Footer className="align-items-center">
                  </Card.Footer>
                </Card>
              ))}
            </div>
          </Offcanvas.Body>
          <Offcanvas.Footer>
            <Button variant="default" onClick={handleClose}>
              Let's do this later
            </Button>
            <Button type="submit" disabled={isLoading} variant="secondary">
              Submit
            </Button>
          </Offcanvas.Footer>
        </Form>
      </Offcanvas>
    </>
  );
};

export default OffcanvasDocUploads;
